import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from '../../../styles/LoginRegister.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import { useVerifyEmail } from '../../../hooks/useVerifyEmail';
import { STORAGE_KEYS } from '../../../constants/api';
import { useEnhancedAuth } from '../../../contexts/EnhancedAuthContext';
import supabase from '../../../utils/supabase';

const VerifyEmail: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const { handleLogin } = useEnhancedAuth();

    const { status, message, countdown, sessionToken, refreshToken } = useVerifyEmail(token, async () => {
        if (sessionToken) {
            sessionStorage.setItem(STORAGE_KEYS.TOKEN, sessionToken);
            // Hand the session to the Supabase client so its background refresh
            // manages it going forward — without this the client never learns a
            // session exists, and the token hard-expires in 1hr with no renewal.
            if (refreshToken) {
                try {
                    await supabase.auth.setSession({ access_token: sessionToken, refresh_token: refreshToken });
                } catch {
                    // Non-fatal — sessionStorage already has a usable token for this hour.
                }
            }
            const destination = await handleLogin();
            navigate(destination ?? '/Account');
        } else {
            navigate('/LoginRegister');
        }
    });

    const getStatusIcon = () => {
        switch (status) {
            case 'verifying':
                return <div className={styles.spinner} />;
            case 'success':
                return <div className={styles.successIcon}>✓</div>;
            case 'error':
            case 'invalid':
                return <div className={styles.errorIcon}>✕</div>;
            default:
                return null;
        }
    };

    const getStatusClass = () => {
        switch (status) {
            case 'success':
                return styles.verificationSuccess;
            case 'error':
            case 'invalid':
                return styles.verificationError;
            default:
                return styles.verificationPending;
        }
    };

    return (
        <div className={styles.container} >
            <div className={styles.verifyEmailCard}>
                <div className={getStatusClass()}>
                    {getStatusIcon()}
                    <h2 className={styles.verifyTitle}>
                        {status === 'verifying' && 'Verifying Email'}
                        {status === 'success' && 'Email Verified!'}
                        {(status === 'error' || status === 'invalid') && 'Verification Failed'}
                    </h2>
                    <p className={styles.verifyMessage}>{message}</p>
                    
                    {status === 'success' && (
                        <div className={styles.redirectInfo}>
                            <p>Redirecting to login in {countdown} seconds...</p>
                            <button
                                className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}
                                onClick={() => navigate('/LoginRegister')}
                            >
                                Go to Login Now
                            </button>
                        </div>
                    )}
                    
                    {(status === 'error' || status === 'invalid') && (
                        <div className={styles.verifyActions}>
                            <button
                                className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}
                                onClick={() => navigate('/LoginRegister')}
                            >
                                Back to Login
                            </button>
                            <button
                                className={`${buttonStyles.button} ${buttonStyles.formSecondary}`}
                                onClick={() => navigate('/ContactUs')}
                            >
                                Contact Support
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail;
